'use client';

import { useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';

const PricingClient = dynamic(() => import('./PricingClient'), {
  ssr: false,
  loading: () => (
    <div className="w-full min-h-[400px] flex items-center justify-center">
      <div className="animate-pulse text-slate-400 text-sm">Cargando planes y precios...</div>
    </div>
  ),
});

interface HomeClientProps {
  countryCode?: string;
  email?: string;
}

export default function HomeClient({ countryCode, email }: HomeClientProps) {
  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-[#eb008b] selection:text-white">
      {/* HEADER / NAVIGATION BAR */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-black/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#eb008b] to-[#8f33c4] flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-[#eb008b]/30">
              W
            </div>
            <span className="text-2xl font-black tracking-tight bg-gradient-to-r from-white via-slate-100 to-[#eb008b] bg-clip-text text-transparent">
              Werds<span className="text-[#eb008b]">AI</span>
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
            <a href="#caracteristicas" className="hover:text-white transition-colors">
              Características
            </a>
            <a href="#beneficios" className="hover:text-white transition-colors">
              Beneficios
            </a>
            <a href="#demostracion" className="hover:text-white transition-colors">
              Demostración
            </a>
            <a href="#planes" className="hover:text-white transition-colors">
              Planes y Precios
            </a>
          </nav>

          <div className="flex items-center gap-4">
            <Link
              href="/login"
              className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-r from-[#eb008b] to-[#8f33c4] hover:opacity-90 text-white shadow-lg shadow-[#eb008b]/25 transition-all transform hover:scale-105 active:scale-95"
            >
              Iniciar Sesión
            </Link>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 overflow-hidden">
        {/* Glowing Background Gradients */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-[#eb008b]/20 to-[#8f33c4]/20 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 text-xs sm:text-sm text-slate-300 backdrop-blur-sm">
            <span className="flex h-2 w-2 rounded-full bg-[#eb008b] animate-pulse" />
            <span>Plataforma Multicanal Empresarial con Inteligencia Artificial</span>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white mb-6 leading-[1.1]">
            Potencia tus ventas y atención en <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-[#eb008b] via-[#a855f7] to-[#8f33c4] bg-clip-text text-transparent">
              WhatsApp, Messenger e Instagram
            </span>
          </h1>

          <p className="max-w-3xl mx-auto text-lg sm:text-xl text-slate-400 mb-10 leading-relaxed">
            Centraliza la atención de tu equipo, automatiza respuestas inteligentes con IA y ejecuta campañas masivas de
            alto impacto sin perder el toque humano.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a
              href="#planes"
              className="w-full sm:w-auto px-8 py-4 rounded-full text-base font-bold bg-gradient-to-r from-[#eb008b] to-[#8f33c4] text-white shadow-xl shadow-[#eb008b]/30 hover:shadow-[#eb008b]/50 transition-all transform hover:-translate-y-0.5 active:translate-y-0"
            >
              Ver Planes y Precios
            </a>
            <Link
              href="/login"
              className="w-full sm:w-auto px-8 py-4 rounded-full text-base font-bold bg-white/10 hover:bg-white/15 text-white border border-white/10 backdrop-blur-sm transition-all"
            >
              Acceder a la Plataforma
            </Link>
          </div>

          {/* MAIN PLATFORM DEMO / PLACEHOLDER */}
          <div className="relative mx-auto max-w-5xl rounded-2xl border border-white/10 bg-slate-950/80 p-3 sm:p-5 shadow-2xl shadow-[#8f33c4]/20 backdrop-blur-xl">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10 px-2 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500/80 inline-block" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/80 inline-block" />
                <span className="w-3 h-3 rounded-full bg-green-500/80 inline-block" />
                <span className="ml-2 font-mono text-slate-500">werdsai-workspace.app</span>
              </div>
              <span className="bg-[#eb008b]/20 text-[#eb008b] px-2.5 py-0.5 rounded-full font-medium">
                Vista Previa del Sistema
              </span>
            </div>

            {/* Video / Visual Placeholder Container */}
            <div className="relative aspect-video w-full rounded-xl bg-gradient-to-br from-slate-900 via-black to-slate-900 border border-white/5 overflow-hidden flex flex-col items-center justify-center group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#eb008b]/10 via-transparent to-[#8f33c4]/10 opacity-50 group-hover:opacity-100 transition-opacity" />

              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-[#eb008b] to-[#8f33c4] flex items-center justify-center text-white shadow-2xl shadow-[#eb008b]/50 group-hover:scale-110 transition-transform mb-4 z-10">
                <svg className="w-8 h-8 ml-1 fill-current" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>

              <p className="text-white font-semibold text-lg z-10">Demostración Interactiva de WerdsAI</p>
              <p className="text-slate-400 text-sm mt-1 z-10">Haz clic para reproducir el video de la plataforma</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES / CARACTERÍSTICAS */}
      <section id="caracteristicas" className="py-24 bg-gradient-to-b from-black via-slate-950 to-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#eb008b] mb-3">
              Solución Integral
            </h2>
            <p className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Diseñado para escalar la comunicación de tu empresa
            </p>
            <p className="mt-4 text-slate-400 text-lg">
              Combina el poder de agentes humanos y asistencia de Inteligencia Artificial en una sola interfaz fluida.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-[#eb008b]/50 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#eb008b] to-[#8f33c4] flex items-center justify-center text-white font-bold text-xl mb-6 shadow-lg shadow-[#eb008b]/20 group-hover:scale-110 transition-transform">
                💬
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Gestión Multi-Línea y Multi-Agente</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Conecta múltiples números de WhatsApp, Instagram y Messenger en un solo lugar. Distribuye conversaciones entre tu equipo de ventas y soporte sin complicaciones.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-[#8f33c4]/50 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#8f33c4] to-[#eb008b] flex items-center justify-center text-white font-bold text-xl mb-6 shadow-lg shadow-[#8f33c4]/20 group-hover:scale-110 transition-transform">
                🤖
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Agentes de IA Automatizados</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Responde a tus clientes al instante las 24 horas del día. Configura agentes inteligentes adaptados a tu negocio que califican clientes y sugieren respuestas óptimas.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-[#eb008b]/50 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#eb008b] to-[#8f33c4] flex items-center justify-center text-white font-bold text-xl mb-6 shadow-lg shadow-[#eb008b]/20 group-hover:scale-110 transition-transform">
                📢
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Campañas de Envío Masivo</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Diseña, personaliza y envía campañas masivas utilizando plantillas aprobadas por Meta con variables dinámicas para llegar a miles de clientes al instante.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-[#8f33c4]/50 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#8f33c4] to-[#eb008b] flex items-center justify-center text-white font-bold text-xl mb-6 shadow-lg shadow-[#8f33c4]/20 group-hover:scale-110 transition-transform">
                🎙️
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Notas de Voz Nativas y Multimedia</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Graba y envía notas de voz con formato nativo de WhatsApp, visualiza ondas de audio interactivas y gestiona imágenes, videos y documentos organizados.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-[#eb008b]/50 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#eb008b] to-[#8f33c4] flex items-center justify-center text-white font-bold text-xl mb-6 shadow-lg shadow-[#eb008b]/20 group-hover:scale-110 transition-transform">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Flujos de Trabajo Inteligentes</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Organiza chats mediante listas dinámicas, arrastra y suelta conversaciones entre categorías y aplica etiquetas personalizadas automáticamente.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-[#8f33c4]/50 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#8f33c4] to-[#eb008b] flex items-center justify-center text-white font-bold text-xl mb-6 shadow-lg shadow-[#8f33c4]/20 group-hover:scale-110 transition-transform">
                🛡️
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Supervisión y Auditoría en Tiempo Real</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Audita conversaciones en modo lectura, gestiona permisos por rol de usuario y mantén un control absoluto sobre el desempeño del equipo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFICIOS / KEY BENEFITS SECTION */}
      <section id="beneficios" className="py-24 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#eb008b] mb-3">
              Por qué elegir WerdsAI
            </h2>
            <p className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Beneficios clave para tu negocio
            </p>
            <p className="mt-4 text-slate-400 text-lg">
              Aumenta la satisfacción de tus clientes y reduce los tiempos de respuesta exponencialmente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl bg-gradient-to-b from-slate-900 to-black border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#eb008b]/10 rounded-full blur-2xl pointer-events-none" />
              <div className="text-4xl font-black text-[#eb008b] mb-4">+300%</div>
              <h3 className="text-xl font-bold text-white mb-2">Aumento en Conversión de Ventas</h3>
              <p className="text-slate-400 text-sm">
                La respuesta inmediata impulsada por IA evita que los prospectos calificados se enfríen o busquen alternativas.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-gradient-to-b from-slate-900 to-black border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#8f33c4]/10 rounded-full blur-2xl pointer-events-none" />
              <div className="text-4xl font-black text-[#8f33c4] mb-4">&lt; 1 min</div>
              <h3 className="text-xl font-bold text-white mb-2">Tiempo Medio de Respuesta</h3>
              <p className="text-slate-400 text-sm">
                Tus agentes humanos y asistentes de IA responden en segundos, garantizando una atención excepcional.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-gradient-to-b from-slate-900 to-black border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#eb008b]/10 rounded-full blur-2xl pointer-events-none" />
              <div className="text-4xl font-black text-[#eb008b] mb-4">24/7</div>
              <h3 className="text-xl font-bold text-white mb-2">Atención Ininterrumpida</h3>
              <p className="text-slate-400 text-sm">
                Tu empresa nunca duerme: capta datos, califica clientes y programa citas a cualquier hora del día o noche.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DEMO & INTERFACE SHOWCASE SECTION */}
      <section id="demostracion" className="py-24 bg-slate-950 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#eb008b] mb-3 block">
                Experiencia de Usuario Fluida
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
                Una consola moderna diseñada para maximizar la productividad
              </h2>
              <p className="text-slate-400 text-base sm:text-lg mb-8 leading-relaxed">
                Diseñada con una arquitectura de alta velocidad, respuestas en tiempo real y componentes intuitivos que permiten a tus agentes gestionar cientos de clientes simultáneamente sin saturación.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#eb008b]/20 text-[#eb008b] flex items-center justify-center font-bold text-sm shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Respuesta inmediata en tiempo real</h4>
                    <p className="text-slate-400 text-sm">Notificaciones al instante sin recargar la página.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#8f33c4]/20 text-[#8f33c4] flex items-center justify-center font-bold text-sm shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Sugerencias de IA con 1-clic</h4>
                    <p className="text-slate-400 text-sm">Usa las sugerencias redactadas por la IA directo en el chat con un solo clic.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#eb008b]/20 text-[#eb008b] flex items-center justify-center font-bold text-sm shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Borradores y adjuntos staged</h4>
                    <p className="text-slate-400 text-sm">Persistencia local automática de mensajes no enviados y múltiples archivos adjuntos.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Image Placeholder Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-slate-900 border border-white/10 p-4 aspect-square flex flex-col justify-between hover:border-[#eb008b]/50 transition-colors group">
                <div className="w-10 h-10 rounded-lg bg-[#eb008b]/20 flex items-center justify-center text-[#eb008b] font-bold text-lg">
                  📱
                </div>
                <div>
                  <p className="text-xs text-[#eb008b] font-mono mb-1">[ Módulo de Chat ]</p>
                  <p className="text-white font-semibold text-sm">Vista de Conversaciones</p>
                  <p className="text-slate-500 text-xs mt-1">Chat en vivo con estado de ventana de 24h</p>
                </div>
              </div>

              <div className="rounded-xl bg-slate-900 border border-white/10 p-4 aspect-square flex flex-col justify-between hover:border-[#8f33c4]/50 transition-colors group">
                <div className="w-10 h-10 rounded-lg bg-[#8f33c4]/20 flex items-center justify-center text-[#8f33c4] font-bold text-lg">
                  ⚙️
                </div>
                <div>
                  <p className="text-xs text-[#8f33c4] font-mono mb-1">[ Configuración IA ]</p>
                  <p className="text-white font-semibold text-sm">Agentes Personalizados</p>
                  <p className="text-slate-500 text-xs mt-1">Ajuste de prompts y temperatura</p>
                </div>
              </div>

              <div className="rounded-xl bg-slate-900 border border-white/10 p-4 aspect-square flex flex-col justify-between hover:border-[#8f33c4]/50 transition-colors group">
                <div className="w-10 h-10 rounded-lg bg-[#8f33c4]/20 flex items-center justify-center text-[#8f33c4] font-bold text-lg">
                  📊
                </div>
                <div>
                  <p className="text-xs text-[#8f33c4] font-mono mb-1">[ Envíos Masivos ]</p>
                  <p className="text-white font-semibold text-sm">Campañas de Difusión</p>
                  <p className="text-slate-500 text-xs mt-1">Previsualizador en tiempo real</p>
                </div>
              </div>

              <div className="rounded-xl bg-slate-900 border border-white/10 p-4 aspect-square flex flex-col justify-between hover:border-[#eb008b]/50 transition-colors group">
                <div className="w-10 h-10 rounded-lg bg-[#eb008b]/20 flex items-center justify-center text-[#eb008b] font-bold text-lg">
                  👥
                </div>
                <div>
                  <p className="text-xs text-[#eb008b] font-mono mb-1">[ Auditoría de Equipo ]</p>
                  <p className="text-white font-semibold text-sm">Asistentes Personales</p>
                  <p className="text-slate-500 text-xs mt-1">Revisión en modo lectura para dueños</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="planes" className="py-24 bg-slate-950 border-t border-white/5 relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#8f33c4]/10 rounded-full blur-3xl pointer-events-none" />

        <PricingClient countryCode={countryCode} email={email} />

      </section>

      {/* FOOTER */}
      <footer className="bg-black border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-[#eb008b] to-[#8f33c4] flex items-center justify-center text-white font-bold text-sm">
              W
            </div>
            <span className="text-xl font-black bg-gradient-to-r from-white via-slate-200 to-[#eb008b] bg-clip-text text-transparent">
              Werds<span className="text-[#eb008b]">AI</span>
            </span>
          </div>

          <p className="text-xs text-slate-500 text-center md:text-left">
            © {new Date().getFullYear()} WerdsAI. Todos los derechos reservados. Plataforma CRM Multicanal e Inteligencia Artificial.
          </p>

          <div className="flex items-center gap-6 text-xs text-slate-400">
            <Link href="/login" className="hover:text-white transition-colors">
              Iniciar Sesión
            </Link>
            <a href="#planes" className="hover:text-white transition-colors">
              Planes
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
